import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookSquare,
  faTwitterSquare,
  faInstagramSquare,
  faLinkedin,
  faYoutubeSquare,
  faSnapchatSquare,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";

const socialMediaIcons = [
  { icon: faFacebookSquare, name: "Facebook", color: "#3b5998" },
  { icon: faTwitterSquare, name: "Twitter", color: "#1DA1F2" },
  { icon: faInstagramSquare, name: "Instagram", color: "#C13584" },
  { icon: faLinkedin, name: "LinkedIn", color: "#0e76a8" },
  { icon: faYoutubeSquare, name: "YouTube", color: "#c4302b" },
  { icon: faSnapchatSquare, name: "Snapchat", color: "#FFFC00" },
  { icon: faTiktok, name: "TikTok", color: "#010101" },
];

function Carousel() {
  return (
    <div className="relative py-32 flex flex-col items-center justify-center overflow-hidden bg-white">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100/40 rounded-full blur-[120px] -z-10 animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-100/40 rounded-full blur-[120px] -z-10 animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="text-center mb-20 px-4 relative z-10">
        <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-full text-xs font-black mb-8 border border-blue-100/50 shadow-sm uppercase tracking-widest animate-bounce-subtle">
          <span className="text-base">✨</span> Social Proof
        </div>
        <h2 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-b from-gray-900 via-gray-800 to-gray-600 mb-6 tracking-tighter leading-[1.1]">
          Connect anywhere — <span className="text-blue-600">everywhere</span>
        </h2>
        <p className="text-gray-500 text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed">
          Braccet integrates with your favorite platforms to streamline your growth and maximize your reach.
        </p>
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6">
        {/* Advanced Glassmorphic Container */}
        <div className="relative overflow-hidden bg-white/30 backdrop-blur-2xl border border-white/80 rounded-[2rem] md:rounded-[4rem] p-8 md:p-16 shadow-[0_32px_120px_-20px_rgba(0,0,0,0.08)]">
          {/* Subtle Inner Glow */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"></div>
          
          {/* Gradient Masks for Smooth Fading */}
          <div className="absolute inset-0 z-20 pointer-events-none">
            <div className="absolute left-0 w-24 md:w-48 h-full bg-gradient-to-r from-white/90 via-white/40 to-transparent"></div>
            <div className="absolute right-0 w-24 md:w-48 h-full bg-gradient-to-l from-white/90 via-white/40 to-transparent"></div>
          </div>
          
          <div className="flex space-x-16 animate-scroll-premium">
            {[...socialMediaIcons, ...socialMediaIcons, ...socialMediaIcons, ...socialMediaIcons].map((item, i) => (
              <div
                key={i}
                className="group relative min-w-[140px] flex flex-col items-center gap-5 transition-all duration-500"
              >
                <div 
                  className="w-20 h-20 rounded-[2rem] flex items-center justify-center shadow-md transition-all duration-500 group-hover:shadow-3xl group-hover:-translate-y-3 group-hover:rotate-3 relative"
                  style={{ background: `white`, border: `1px solid ${item.color}15` }}
                >
                  {/* Subtle Glow on Hover */}
                  <div 
                    className="absolute inset-0 rounded-[2rem] mix-blend-multiply opacity-0 group-hover:opacity-10 transition-opacity duration-500"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  
                  <FontAwesomeIcon
                    icon={item.icon}
                    className="text-4xl transition-all duration-500 group-hover:scale-110 z-10"
                    style={{ color: item.color }}
                  />
                  
                  {/* Card Shine Effect */}
                  <div className="absolute inset-0 rounded-[2rem] overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                    <div className="absolute top-[-100%] left-[-100%] w-[300%] h-[300%] bg-gradient-to-br from-white/40 via-transparent to-transparent rotate-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  </div>
                </div>
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] group-hover:text-gray-900 transition-colors duration-300">
                  {item.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes scroll-premium {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .animate-scroll-premium {
          animation: scroll-premium 40s linear infinite;
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 3s ease-in-out infinite;
        }
        .animate-scroll-premium:hover {
          animation-play-state: paused;
        }
        .shadow-3xl {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
}

export default Carousel;
